import { useState, useMemo, useContext } from 'react'
import { observer } from 'mobx-react'
import { Input, Tree } from 'antd'
import { MenuUnfoldOutlined, MenuFoldOutlined, AlertOutlined, DisconnectOutlined } from '@ant-design/icons'
import { TreeContext, LoadableButton, PictureThumb, postMsg } from 'src/components'
import { $consts } from 'src/plugins'

const TreePageEmpty = observer(() => {
  const tree = useContext(TreeContext)

  const { treeConfig, treeData, enableBtns } = tree
  const { Nav_PageTree = {} } = treeConfig
  const { CAN_ADD_NODE } = Nav_PageTree

  if (!treeData.length && CAN_ADD_NODE) {
    const addBtns = enableBtns.filter((btn: any) => {
      return btn.BTN_TYPE === $consts['TABLE/BTN_TYPE_ADD'] || btn.IS_SAME_LEVEL
    })
    const sameLevelBtn = addBtns.find((btn: any) => btn.IS_SAME_LEVEL) ?? addBtns[0]
    if (sameLevelBtn) {
      return (
        <LoadableButton
          pageType={$consts['TABLE/FORM_TYPE_TREE_PAGE']}
          locateBtn={$consts['TABLE/BTN_LOCATE_HEADER']}
          config={sameLevelBtn}
        />
      )
    }
    return <p>没有配置新增按钮</p>
  }

  return null
})

const TreePageTree = () => {
  const tree = useContext(TreeContext)

  // 树展开收起
  const [collapsed, setCollapsed] = useState(false)

  // 树单选中
  const handleTreeNodesSelect = (selectedKeys: string[]) => {
    tree.setSelectedKeys(selectedKeys)
  }

  // 树复选中
  const handleTreeNodesCheck = ({ checked: selectedKeys }: any) => {
    const { Nav_PageTree = {} } = tree.treeConfig
    const { MULTI_CHECK_COUNT } = Nav_PageTree
    if (MULTI_CHECK_COUNT && selectedKeys.length > MULTI_CHECK_COUNT) {
      postMsg(`最多可选${MULTI_CHECK_COUNT}个节点`, 'error')
      return
    }
    handleTreeNodesSelect(selectedKeys)
  }

  // 构造树数据
  const validTreeData = useMemo(() => {
    const renderTreeData = (treeData: any[]): any[] => {
      return treeData.map(item => {
        const { Children, Node } = item
        const {
          NAME,
          IS_EXIST_ALARM: alarm, // 节点异常
          CHILD_EXIST_ALARM: childAlarm, // 子节点异常
          APPLIANCE_ID, // 器具 ID
          Nav_Appliance, // 器具详情
        } = Node
        const disconnect = APPLIANCE_ID !== null && Nav_Appliance?.IS_REPORT === false

        const alert = `${NAME}${alarm ? '(节点异常)' : childAlarm ? '(存在子节点异常)' : disconnect ? '(器具未采集数据)' : ''}`
        const color = alarm ? 'red' : childAlarm ? 'orange' : disconnect ? 'rgba(170, 170, 170)' : 'inhert'
        const title = (
          <span title={alert}>
            <span className='pd-r4' style={{ color }}>{NAME}</span>
            {alarm || childAlarm ? <AlertOutlined style={{ color }} /> : disconnect ? <DisconnectOutlined style={{ color }} /> : null}
          </span>
        )
        if (Children) {
          return {
            key: Node.ID,
            title,
            icon: <PictureThumb picFilter={tree.treeConfig.Nav_PicFilter} targetData={item} thumbStyle={{ padding: 4 }} />,
            children: renderTreeData(Children)
          }
        }
        return {
          key: Node.ID,
          title,
          icon: <PictureThumb picFilter={tree.treeConfig.Nav_PicFilter} targetData={item} thumbStyle={{ padding: 4 }} />
        }
      })
    }

    return renderTreeData(tree.visibleTreeData)
  }, [tree.visibleTreeData])

  const { treeConfig, treeData, pageDomSize, selectedNodes, selectedNodesToolBtns } = tree
  const { Nav_PageTree = {} } = treeConfig
  const { LABEL_NAME, MULTI_CHECK } = Nav_PageTree

  if (!pageDomSize.clientHeight) return null

  const treeHeight = pageDomSize.clientHeight - 24 - 24 - 34 - 28 - 40 - 8
  const isEmptyTree = !LABEL_NAME || !treeData.length
  return (
    <div className={`tree-page__tree ${collapsed ? 'collapsed' : ''}`}>
      <div onClick={() => setCollapsed(!collapsed)} className='tree-page__tree-collapse'>
        {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
      </div>
      <div onClick={() => collapsed && setCollapsed(false)} className='tree-page__tree-wrap'>
        <div className='tree-page__tree-title'>{LABEL_NAME}</div>
        <div className='tree-page__tree-tools'>
          <div className='tree-page__tree-toolsTitle'>当前选中：{selectedNodes.length > 1 ? `${selectedNodes.length}个` : (selectedNodes[0]?.Node.NAME || '暂无')}</div>
          <div className='tree-page__tree-toolsBtn'>
            {
              isEmptyTree
                ? <TreePageEmpty />
                : selectedNodesToolBtns.map((btn: any, index: number) => {
                  return (
                    <LoadableButton
                      key={`${btn.ID}_${index}`}
                      pageType={$consts['TABLE/FORM_TYPE_TREE_PAGE']}
                      locateBtn={$consts['TABLE/BTN_LOCATE_HEADER']}
                      config={btn}
                    />
                  )
                })
            }
          </div>
        </div>
        <Input.Search
          allowClear
          onSearch={val => tree.setSearchKey(val)}
          placeholder='关键字搜索'
          className='tree-page__tree-search'
        />
        <div className='tree-page__tree-main'>
          {
            !isEmptyTree
              ? (
                <Tree
                  showIcon
                  checkStrictly
                  height={treeHeight}
                  checkable={MULTI_CHECK}
                  checkedKeys={tree.selectedKeys}
                  selectable={!MULTI_CHECK}
                  selectedKeys={tree.selectedKeys}
                  treeData={validTreeData}
                  {...tree.defaultExpand}
                  onSelect={handleTreeNodesSelect}
                  onCheck={handleTreeNodesCheck}
                />
              )
              : null
          }
        </div>
      </div>
    </div>
  )
}

export default observer(TreePageTree)